import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import packageJson from '../../../package.json';
import {
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import logo2 from "../../Assets/favicon.ico";
import logo from "../../Assets/logo.png";
import "../../Style/Auth.css";
import AuthService from "../../Api/AuthService";
import permissionService from "../../Permission/Permission";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // field-level errors
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // branch selection state
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [token, setToken] = useState("");

  // Check if user is already logged in with remember me on component mount
  useEffect(() => {
    const userRemember = localStorage.getItem("user_rem");
    const userData = localStorage.getItem("user");
    
    if (userRemember === "true" && userData) {
      // User has remember me enabled and valid session
      try {
        const user = JSON.parse(userData);
        const firstAccessibleScreen = checkUserPermissions(user);
        if (firstAccessibleScreen) {
          navigate(`/${firstAccessibleScreen === 'dashboard' ? 'dashboard' : getScreenRoute(firstAccessibleScreen)}`);
        }
      } catch (err) {
        // Invalid user data, clear storage
        localStorage.removeItem("user");
        localStorage.removeItem("user_rem");
      }
    }
  }, []);

  const validateForm = () => {
    let valid = true;
    setEmailError("");
    setPasswordError("");

    if (!email.trim()) {
      setEmailError("User name is required");
      valid = false;
    }
    else if (email.length < 3) {
      setEmailError("User name must be at least 3 characters");
      valid = false;
    }

    if (!password.trim()) {
      setPasswordError("Password is required");
      valid = false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      valid = false;
    }

    return valid;
  };

  const checkUserPermissions = (userData) => {
    // Initialize permission service with user data
    permissionService.refreshPermissions();

    // Get accessible screens
    const accessibleScreens = permissionService.getAccessibleScreens();

    if (accessibleScreens.length === 0) {
      toast.error("You don't have any permissions to access to system", {
        autoClose: 3000,
        position: "top-center"
      });
      return false;
    }

    // Find the first accessible screen to redirect to
    const firstAccessibleScreen = getFirstAccessibleScreen(accessibleScreens);
    return firstAccessibleScreen;
  };

  const getFirstAccessibleScreen = (accessibleScreens) => {
    // Define priority of screens (dashboard first, then others)
    const screenPriority = [
      'dashboard',
      'docmaster',
      'reports',
      'print',
      'users',
      'location'
    ];

    // Find the first accessible screen based on priority
    for (const screen of screenPriority) {
      if (accessibleScreens.includes(screen)) {
        return screen;
      }
    }

    // If no priority screen found, return the first accessible screen
    return accessibleScreens[0];
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoginLoading(true);
    try {
      const loginRes = await AuthService.login(email, password);

      // Temporarily keep login response
      setToken(loginRes.accessToken); // needed for selectBranch

      if (loginRes.branches?.length === 1) {
        // Only one branch → auto select
        const branchId = loginRes.branches[0].branch_id;
        const branchRes = await AuthService.selectBranch(branchId, loginRes.accessToken);

        // ✅ Store entire login response but replace accessToken with branch token
        const finalData = { ...loginRes, accessToken: branchRes.accessToken, branch: branchRes.branch };
        localStorage.setItem("user", JSON.stringify(finalData));
        
        // Store remember me preference separately
        localStorage.setItem("user_rem", rememberMe.toString());

        // Check user permissions
        const firstAccessibleScreen = checkUserPermissions(finalData);

        if (firstAccessibleScreen) {
          var response = `/${firstAccessibleScreen === 'dashboard' ? 'dashboard' : getScreenRoute(firstAccessibleScreen)}`;
          navigate(response);
          toast.success("Login successful 🎉", { autoClose: 3000 });
        } else {
          // User has no permissions - they'll see the toast message from checkUserPermissions
          // Wait a bit before logging them out so they can see the message
          setTimeout(() => {
            localStorage.removeItem("user");
            localStorage.removeItem("user_rem");
            setLoginLoading(false);
          }, 3000);
        }

      } else if (loginRes.branches?.length > 1) {
        // Multiple branches → show selection
        localStorage.setItem("user", JSON.stringify(loginRes));
        setBranches(loginRes.branches);
        setToken(loginRes.accessToken); // keep login token for branch selection
      } else {
        toast.error("No branches assigned to this user", { autoClose: 3000 });
      }
    } catch (err) {
      const message =
        err.message === "Failed to fetch"
          ? "Unable to connect. Please check your internet connection."
          : err.message || "Login failed. Please try again.";
      toast.error(message, { autoClose: 3000 });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleBranchSelect = async () => {
    if (!branchId) {
      toast.error("Please select a branch", { autoClose: 3000 });
      return;
    }

    setBranchLoading(true);
    try {
      const branchRes = await AuthService.selectBranch(branchId, token);

      // ✅ Store full login response but update accessToken with branch token
      const storedLogin = JSON.parse(localStorage.getItem("user")) || {};
      const finalData = { ...storedLogin, accessToken: branchRes.accessToken, branch: branchRes.branch };

      localStorage.setItem("user", JSON.stringify(finalData));
      
      // Store remember me preference separately
      localStorage.setItem("user_rem", rememberMe.toString());

      // Check user permissions after branch selection
      const firstAccessibleScreen = checkUserPermissions(finalData);

      if (firstAccessibleScreen) {
        toast.success("Branch selected successfully 🎉", { autoClose: 3000 });
        navigate(`/${firstAccessibleScreen === 'dashboard' ? 'dashboard' : getScreenRoute(firstAccessibleScreen)}`);
      } else {
        // User has no permissions
        setTimeout(() => {
          localStorage.removeItem("user");
          localStorage.removeItem("user_rem");
          setBranchLoading(false);
        }, 3000);
      }
    } catch (err) {
      toast.error(err.message || "Branch selection failed", { autoClose: 3000 });
      setBranchLoading(false);
    }
  };

  const getScreenRoute = (screenName) => {
    const routeMap = {
      'dashboard': 'dashboard',
      'docmaster': 'doc-master-list',
      'reports': 'report',
      'print': 'print',
      'users': 'user_management',
      'location': 'location'
    };

    return routeMap[screenName] || 'dashboard';
  };

  return (
    <div className="auth-container">
      {branches.length === 0 ? (
        // --- LOGIN FORM ---
        <>
          <div className="auth-image">
            <img src={logo} alt="Login Illustration" />
            {/* Circular image below the logo */}
            <div className="circular-image-wrapper">
              <img src={logo2} alt="Circular Illustration" className="circular-image" />
            </div>
          </div>
          <div className="auth-left">
            <div className="auth-form">
              <h3>Log in to your account</h3>
              <p className="auth-subtitle">
                Welcome back! Please enter your details.
              </p>

              <form onSubmit={handleLogin}>
                {/* Email */}
                <div className="form-group">
                  <label>User name or Email</label>
                  <input
                    type="text"
                    placeholder="Enter your user name or email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loginLoading}
                  />
                  {emailError && (
                    <div className="error-message">{emailError}</div>
                  )}
                </div>

                {/* Password */}
                <div className="form-group" style={{ position: "relative" }}>
                  <label>Password</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loginLoading}
                  />
                  <span
                    onClick={() => !loginLoading && setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: passwordError ? "55%" : "75%",
                      transform: "translateY(-50%)",
                      cursor: loginLoading ? "not-allowed" : "pointer",
                      color: loginLoading ? "#ccc" : "#555",
                      opacity: loginLoading ? 0.6 : 1,
                    }}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                  {passwordError && (
                    <div className="error-message">{passwordError}</div>
                  )}
                </div>

                <div className="form-options">
                  <label className="remember-me">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loginLoading}
                    />
                    Remember me
                  </label>
                </div>

                <button
                  type="submit"
                  className="auth-button"
                  disabled={loginLoading}
                >
                  {loginLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <CircularProgress size={16} color="inherit" />
                      Signing in...
                    </div>
                  ) : (
                    `Sign in `
                  )}
                </button>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '10px'
                }}>
                  {`Version ${packageJson.version}`}
                </div>

              </form>
            </div>
          </div>
        </>
      ) : (
        // --- BRANCH SELECTION FORM ---
        <>
          <div className="auth-image">
            <img src={logo} alt="Company Logo" />
            {/* Circular image below the logo */}
            <div className="circular-image-wrapper">
              <img src={logo2} alt="Circular Illustration" className="circular-image" />
            </div>
          </div>
          <div className="auth-left">
            <div className="auth-form">
              <h3>Select Your Branch</h3>
              <p className="auth-subtitle">
                Please choose your branch to continue
              </p>

              <div className="form-group branch-select-container">
                <FormControl fullWidth disabled={branchLoading} className="auth-select">
                  <InputLabel id="branch-select-label">Branch</InputLabel>
                  <Select
                    labelId="branch-select-label"
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                    displayEmpty
                    sx={{
                      '& .MuiSelect-select': {
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        backgroundColor: '#f9f9f9',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select a branch
                    </MenuItem>
                    {branches.map((branch) => (
                      <MenuItem key={branch.branch_id} value={branch.branch_id}>
                        {branch.branch_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <button
                onClick={handleBranchSelect}
                className="auth-button"
                disabled={branchLoading || !branchId}
              >
                {branchLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <CircularProgress size={16} color="inherit" />
                    Processing...
                  </div>
                ) : (
                  "Continue to Dashboard"
                )}
              </button>

              <div className="back-to-login">
                <button
                  onClick={() => {
                    setBranches([]);
                    setBranchId("");
                    setToken("");
                  }}
                  className="auth-button"
                  disabled={branchLoading}
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Login;